import * as React from "react";

import { useNavigation } from "@/providers";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { AssociationRecord } from "@/shared/domain/association";
import { MessagePathRecord, TopicRecord } from "@/shared/domain/topics";
import { getWorkspaceInput } from "@/shared/domain/workspaces/WorkspaceBuilder";
import { SearchParams } from "@/shared/environment";
import { Goto } from "@/shared/navigation/Goto";
import { determineDefaultPanelType } from "@/shared/state/visualization/schema/v1";
import { nanoSecToSecStr } from "@/shared/time";

interface MessagePathLinkProps {
  messagePath: MessagePathRecord;
  topic: TopicRecord;
  topicAssociation: AssociationRecord;
  linkPrefix?: string;
  className?: string;

  /**
   * If provided, the file will be opened at the given time.
   * Otherwise, the file will be opened at the start of the file.
   *
   * `startTime` is in nanoseconds since the UNIX epoch.
   **/
  startTime?: bigint;
}

export const MessagePathLink: React.FC<MessagePathLinkProps> = ({
  messagePath,
  topic,
  topicAssociation,
  linkPrefix,
  className,
  startTime,
}) => {
  const { goto } = useNavigation();

  // Build the visualize URL for the href (for accessibility and browser features)
  const searchParams = new URLSearchParams();
  searchParams.append("file", topicAssociation.association_id);

  if (startTime) {
    searchParams.set(SearchParams.TIMESTAMP, nanoSecToSecStr(startTime));
  }

  const handleClick = (e: React.MouseEvent) => {
    // Prevent default navigation and use goto.visualize instead to handle layout state
    e.preventDefault();

    const panelType = determineDefaultPanelType(
      messagePath.canonical_data_type,
    );

    goto.visualize({
      dataSource: {
        files: [topicAssociation.association_id],
      },
      params: {
        layout: getWorkspaceInput(panelType, topic, messagePath),
        t: startTime,
      },
    });
  };

  return (
    <RobotoLinkHoverUnderline
      to={`${Goto.route.Visualize}?${searchParams}`}
      variant="body2"
      className={className}
      onClick={handleClick}
    >
      {linkPrefix}
      {messagePath.message_path}
    </RobotoLinkHoverUnderline>
  );
};
